import React, {useCallback, useContext, useLayoutEffect, useState} from 'react';
import {useNavigation} from '@react-navigation/native';
import {Linking, Platform, Text, View, ScrollView} from 'react-native';
import MainStyle from '../../MainStyle';
import {colors} from '../assets/styles/colors';
import SupportGuideHeader from '../components/SupportGuideHeader';
import Button from '../components/Button';
import {StoreContext} from '../components/StoreContextProvider';
import {LANDING_PAGE_URL, SESSION_EXPIRE_TIME, SupportMail, BrandName, isFullSwing} from '../constants';
import SelfLimitationModal from "../modals/SelfLimitationModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import {User} from "../../generated/smg_service_pb";
import Div from "../lib/Div";
import FSHeader from "../components/FSHeader";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";

type Props = {
  onExit?: () => void;
};

const SelfExclusionScreen = ({onExit: onExitOverride}: Props = {}) => {
  const Context = useContext(StoreContext);
  const {user, session, cancelModalConfig} = Context;
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const [showModal, setShowModal] = useState(false);

  const onExit = useCallback(() => {
    if (onExitOverride) {
      onExitOverride();
      return;
    }

    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('challenges');
  }, [navigation, onExitOverride]);

  const openLink = (url: string) => {
    if (Platform.OS === 'web') window.open(url, '_blank');
    else Linking.openURL(url).then();
  };

  useLayoutEffect(() => {
    if (!session || !user) {
      Context.setLoadingOverlay(true);

      (async () => {
        const time = await AsyncStorage.getItem('lastActiveTime');

        if (time && moment().diff(moment(time), 'minutes') > SESSION_EXPIRE_TIME) {
          Context.logout().then(() => {
            if (Platform.OS === 'web') {
              window.open(LANDING_PAGE_URL, '_self');
            }
            Context.setLoadingOverlay(false);
          });

        } else {
          const val = await AsyncStorage.getItem('session');
          let curUser: User | undefined;

          if (val) {
            const Session = JSON.parse(val);
            Context.setSession(Session);

            try {
              curUser = await Context.getUser(Session);

            } catch (e: any) {
              Context.setCancelModalConfig({
                visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
                onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
              });
            }
          }

          Context.setLoadingOverlay(false);
          if (!val || !curUser || !curUser.userId) {
            Context.logout().then(() => {
              if (Platform.OS === 'web') {
                window.open(LANDING_PAGE_URL, '_self');
              }
            });
          }
        }
      })();
    }
  }, []);

  return (
    <>
      {isFullSwing ? (
        <FSHeader
          title="SELF LIMITATION" goBack={onExit} hideLogo hideGameLogo hideAccount hideBalance hideMenu px={20}
        />
      ) : (
        <SupportGuideHeader title='SELF LIMITATION' onExit={onExit}/>
      )}

      <ScrollView keyboardShouldPersistTaps="handled"
                  contentContainerStyle={[MainStyle.min_h_full, {backgroundColor: isFullSwing ? 'transparent' : colors.bg_contest, paddingBottom: 27}]}>
        <Div maxW={1040} w="100%" alignSelf="center" px={36}>
          <Div row justifyContent="center" alignItems="center" w="100%" mt={40} mb={37}>
            <Button
              w={240} h={40} bg={colors.unselect} borderWidth={1} borderColor={colors.popupDropBg} radius={40}
              fontSize={16} text="START SELF LIMITATION" onPress={() => setShowModal(true)}
            />
          </Div>

          <View>
            <Text style={styles.title_s}>
              {`SELF-EXCLUSION INFORMATION\n${BrandName.toUpperCase()}, INC.`}
            </Text>

            <Text style={styles.title_s}>GENERAL</Text>

            <Text style={styles.text_s}>
              We want all of our user to enjoy our games responsibly. We are committed to maintaining a safe and
              accountable experience for our users. All of our users should:
            </Text>

            <Div row>
              <Text style={styles.dot_s}>&#8226;</Text>
              <Text style={[...styles.text_s, MainStyle.m_b_0]}>
                Consider different ways that they can limit their play;
              </Text>
            </Div>

            <Div row>
              <Text style={styles.dot_s}>&#8226;</Text>
              <Text style={[...styles.text_s, MainStyle.m_b_0]}>
                Not participate in challenges if it interferes with your daily life and/or responsibilities;
              </Text>
            </Div>

            <Div row>
              <Text style={styles.dot_s}>&#8226;</Text>
              <Text style={[...styles.text_s, MainStyle.m_b_0]}>
                Not enter challenges if you are recovering from a dependency and/or under the influence of alcohol,
                drugs, or other substances; and
              </Text>
            </Div>

            <Div row mb={34}>
              <Text style={styles.dot_s}>&#8226;</Text>
              <Text style={[...styles.text_s, MainStyle.m_b_0]}>
                Not enter challenges if your primary goal is to recover losses.
              </Text>
            </Div>

            <Text style={styles.title_s}>SELF-LIMITATION PROCESS</Text>

            <Text style={styles.text_s}>
              All users have the option of self-excluding by selecting the “Self-Limitation” option in the application
              menu. Once you have selected the Self-Limitation option from the application menu, you will have the
              option to select to the duration of the limitation. The minimum self-limitation time is six (6) months.
            </Text>

            <Text style={styles.text_s}>
              Once you have selected the Self-Limitation option from the menu and the duration of the limitation, and
              have confirmed once prompted to do so, the limitation becomes irrevocable until the expiration of the
              limitation term. During the limitation period (i) you will not be able to access the application; (ii) you
              will not receive any promotional materials from {BrandName}; (iii) you will be prohibited from making
              deposits and entering challenges; (iv) any new accounts you create or attempt to create in circumvention
              of your self-limitation that are detected by us will be closed. If you have questions about how reward
              points, scheduled payments, and remaining balances are handled, please contact us at <Text
              style={MainStyle.t_d_l_u} onPress={() => openLink(`mailto:${SupportMail}`)}>{SupportMail}</Text>.
            </Text>

            <Text style={styles.title_s}>REINSTATEMENT</Text>

            <Text style={styles.text_s}>
              {BrandName} will not notify you once your self-limitation period expires. If you would like to have your
              account reinstated after expiration of your self-limitation period, you may contact us at <Text
              style={MainStyle.t_d_l_u} onPress={() => openLink(`mailto:${SupportMail}`)}>{SupportMail}</Text>.
            </Text>

            <Text style={styles.title_s}>NOTICE</Text>

            <Text style={styles.text_s}>
              All users of {BrandName}’s games and challenge and promotions entrants must agree and are subject
              to {BrandName}’ General Terms and Conditions, Promotions <Text style={styles.small_link}
                                                                             onPress={() => navigation.navigate('TermsAndConditions')}>Terms
              and Conditions</Text> (if applicable, and any applicable Key Terms for such promotions or specific games),
              and {BrandName}’ <Text style={styles.small_link} onPress={() => navigation.navigate('PrivacyPolicy')}>Privacy
              Policy</Text> (collectively, the “Rules”). It is your responsibility to read and understand the Rules
              before beginning play.
            </Text>
          </View>
        </Div>
      </ScrollView>

      <SelfLimitationModal visible={showModal} onClose={() => setShowModal(false)}/>
    </>
  );
};

const styles = {
  title_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_16, MainStyle.m_b_15, MainStyle.t_t_u],
  text_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.m_b_15, MainStyle.l_h_16],
  dot_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.l_h_16, MainStyle.m_x_8],
  small_link: [MainStyle.Barlow700, MainStyle.t_d_l_u]
};

export default SelfExclusionScreen;
